import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translations from '@translationKeys'
import { Nullable } from '@types'

import { POneEvent } from '@modules/hub/types'
import { Close } from '@mui/icons-material'

import ArrowTop from '../ArrowLeft'
import ArrowBottom from '../ArrowRight'
import {
  EventTile,
  EventTileEventPartneringLabel,
  EventTileGoToLabel,
  EventTileLabelArrow,
  EventTileLabelContainer,
  EventTileLabelText,
} from '../TopSection/index.styles'
import { ArrowBox, PopupCloseButtonBox, PopupContent, PopupDimmer, PopupInner, PopupRoot } from './index.styles'

const SCROLL_VALUE = 220
const MAX_TILES_WITHOUT_SCROLLING = 2
/**
 * This padding is here to ensure that the scrolling arrows on the left
 * and right side of the tab container are hidden and not blocking the
 * right and left-most tabs once the scroll enters this threshold.
 */
const VERTICAL_PADDING_THRESHOLD = 20

interface PastEventsPopupMobileProps {
  eventsList: POneEvent[]
  onClose: () => void
}

const PastEventsPopupMobile = ({ eventsList, onClose }: PastEventsPopupMobileProps) => {
  const { t } = useTranslation()

  const ref = useRef<Nullable<HTMLDivElement>>(null)

  const [isTopArrowDisabled, setIsTopArrowDisabled] = useState(true)
  const [isBottomArrowDisabled, setIsBottomArrowDisabled] = useState(false)
  const [sliderWidth, setSliderWidth] = useState(0)

  useEffect(() => {
    if (eventsList.length <= MAX_TILES_WITHOUT_SCROLLING) {
      setIsTopArrowDisabled(true)
      setIsBottomArrowDisabled(true)
    }
  }, [eventsList])

  useEffect(() => {
    if (ref?.current) {
      setSliderWidth(Number(ref?.current?.scrollHeight || 0) - Number(ref?.current?.offsetHeight || 0))
    }
  }, [ref?.current])

  const handleScroll = (scrollOffset: number) => {
    if (ref?.current) {
      ref.current.scrollTop += scrollOffset
    }
  }

  const onScroll = () => {
    if (ref?.current) {
      setIsTopArrowDisabled(ref?.current?.scrollTop < VERTICAL_PADDING_THRESHOLD)
      setIsBottomArrowDisabled(ref?.current?.scrollTop > sliderWidth - VERTICAL_PADDING_THRESHOLD)
    }
  }

  return (
    <PopupRoot data-testid="past-events-mobile-popup">
      <PopupDimmer onClick={onClose} />
      <PopupInner>
        <PopupCloseButtonBox onClick={onClose} data-testid="past-events-mobile-close-button">
          <Close />
        </PopupCloseButtonBox>
        <ArrowBox
          $disabled={isTopArrowDisabled}
          onClick={() => handleScroll(-SCROLL_VALUE)}
          data-testid="past-events-mobile-top-arrow"
        >
          <ArrowTop disabled={isTopArrowDisabled} />
        </ArrowBox>

        <PopupContent ref={ref} onScroll={onScroll} data-testid="past-events-mobile-content">
          {eventsList.map(event => (
            <EventTile
              key={event?.id}
              bannerUrl={event?.banner}
              href={event ? `/event/${event?.id}` : undefined}
              name={event?.name}
              $minWidth="340px"
              $marginBottom="0"
              overlay={
                <EventTileLabelContainer>
                  <EventTileLabelText>
                    <EventTileGoToLabel>{t(translations.GO_TO)}</EventTileGoToLabel>{' '}
                    <EventTileEventPartneringLabel>{t(translations.PAST_EVENT)}</EventTileEventPartneringLabel>
                  </EventTileLabelText>
                  <EventTileLabelArrow />
                </EventTileLabelContainer>
              }
            />
          ))}
        </PopupContent>

        <ArrowBox
          $disabled={isBottomArrowDisabled}
          onClick={() => handleScroll(SCROLL_VALUE)}
          data-testid="past-events-mobile-bottom-arrow"
        >
          <ArrowBottom disabled={isBottomArrowDisabled} />
        </ArrowBox>
      </PopupInner>
    </PopupRoot>
  )
}

export default PastEventsPopupMobile
